import React from "react";
import { useParams } from "react-router-dom";
import {
  Box,
  Typography,
  Button,
  CircularProgress,
  Alert,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  MenuItem,
  Select,
  Menu,
  IconButton,
} from "@mui/material";
import {
  fetchLearningPaths,
  createLearningPath,
  renameLearningPath,
  deleteLearningPath,
  fetchLearningPlan,
  fetchAvailablePlanContent,
  addLearningPlanItem,
  removeLearningPlanItem,
  reorderLearningPlanItems,
  setItemGating,
  setItemDualShow,
  setPlanPacing,
  unlockNextPlanItem,
} from "../../api/backend";

// ── Design tokens (Beacon) ───────────────────────────────────────────────────
const T = {
  primary: "var(--color-primary)",
  ink: "var(--color-ink)",
  ink65: "rgba(31,22,51,.65)",
  ink55: "rgba(31,22,51,.55)",
  ink45: "rgba(31,22,51,.45)",
  ink40: "rgba(31,22,51,.4)",
  ink35: "rgba(31,22,51,.35)",
  line: "var(--color-hairline-cloud)",
  lineCool: "var(--color-hairline-cool)",
  pressLight: "var(--color-surface-press-light)",
  violetLink: "var(--color-accent-violet)",
  panelBg: "#fafafc",
  shadowMd: "var(--elevation-2)",
  display: "var(--font-display)",
  primaryTint: "rgba(21,15,35,.08)",
  danger: "#c23d68",
  dangerBg: "rgba(250,127,170,.15)",
};

const GLOBAL_SCOPE = "GLOBAL";

const TYPE_META = {
  DOCUMENT: { label: "Document", bankLabel: "Documents", dot: "#79628c", chipBg: "rgba(121,98,140,.15)", chipFg: "#79628c" },
  TEST: { label: "Test", bankLabel: "Tests", dot: "#c23d68", chipBg: "rgba(250,127,170,.18)", chipFg: "#c23d68" },
  SKILL_SHEET: { label: "Skill Sheet", bankLabel: "Skill Sheets", dot: "#4a5a12", chipBg: "rgba(194,239,78,.35)", chipFg: "#4a5a12" },
};
const TYPE_ORDER = ["DOCUMENT", "TEST", "SKILL_SHEET"];

// ── Icons ────────────────────────────────────────────────────────────────────
// Plain <svg> — not `Box component="svg"`, which consumes width/height as (broken)
// MUI system-style props and leaves the element unsized.
const Svg = ({ width, height, style, ...rest }) => {
  const px = (v) => (v == null ? undefined : `${parseFloat(v)}px`);
  return (
    <svg
      xmlns="http://www.w3.org/2000/svg"
      width={width}
      height={height}
      style={{ flexShrink: 0, display: "block", width: px(width), height: px(height), ...style }}
      {...rest}
    />
  );
};
const IconDrag = () => (
  <Svg width="14" height="14" viewBox="0 0 24 24" fill="currentColor">
    <circle cx="8" cy="6" r="1.4" /><circle cx="8" cy="12" r="1.4" /><circle cx="8" cy="18" r="1.4" />
    <circle cx="16" cy="6" r="1.4" /><circle cx="16" cy="12" r="1.4" /><circle cx="16" cy="18" r="1.4" />
  </Svg>
);
const IconUp = () => (
  <Svg width="10" height="10" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="3" strokeLinecap="round" strokeLinejoin="round"><polyline points="18 15 12 9 6 15" /></Svg>
);
const IconDown = () => (
  <Svg width="10" height="10" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="3" strokeLinecap="round" strokeLinejoin="round"><polyline points="6 9 12 15 18 9" /></Svg>
);
const IconTrash = () => (
  <Svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><polyline points="3 6 5 6 21 6" /><path d="M19 6l-1 14a2 2 0 0 1-2 2H8a2 2 0 0 1-2-2L5 6" /></Svg>
);
const IconLock = () => (
  <Svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><rect x="3" y="11" width="18" height="11" rx="2" /><path d="M7 11V7a5 5 0 0 1 10 0v4" /></Svg>
);
const IconMore = () => (
  <Svg width="14" height="14" viewBox="0 0 24 24" fill="currentColor"><circle cx="5" cy="12" r="1.8" /><circle cx="12" cy="12" r="1.8" /><circle cx="19" cy="12" r="1.8" /></Svg>
);
const IconPlus = () => (
  <Svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"><line x1="12" y1="5" x2="12" y2="19" /><line x1="5" y1="12" x2="19" y2="12" /></Svg>
);
const IconRoute = () => (
  <Svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round"><circle cx="6" cy="19" r="2.5" /><circle cx="18" cy="5" r="2.5" /><path d="M8 18h6a4 4 0 0 0 4-4V9a4 4 0 0 0-4-4h-1" /></Svg>
);
const IconPencil = () => (
  <Svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M12 20h9" /><path d="M16.5 3.5a2.121 2.121 0 0 1 3 3L7 19l-4 1 1-4 12.5-12.5z" /></Svg>
);

// ── Toggle switch ────────────────────────────────────────────────────────────
function Toggle({ checked, onChange, disabled }) {
  return (
    <Box
      component="label"
      sx={{
        position: "relative", display: "inline-block", width: 38, height: 22, flexShrink: 0,
        cursor: disabled ? "default" : "pointer", opacity: disabled ? 0.5 : 1,
      }}
    >
      <Box
        component="input"
        type="checkbox"
        checked={!!checked}
        disabled={disabled}
        onChange={(e) => onChange(e.target.checked)}
        sx={{ opacity: 0, width: 0, height: 0, position: "absolute" }}
      />
      <Box
        sx={{
          position: "absolute", inset: 0, borderRadius: "999px",
          bgcolor: checked ? T.primary : T.line, transition: "background .2s",
        }}
      />
      <Box
        sx={{
          position: "absolute", top: 3, left: 3, width: 16, height: 16, borderRadius: "50%",
          bgcolor: "#fff", boxShadow: "0 1px 3px rgba(0,0,0,.25)", transition: "transform .2s",
          transform: checked ? "translateX(16px)" : "none", pointerEvents: "none",
        }}
      />
    </Box>
  );
}

function TypeChip({ contentType }) {
  const meta = TYPE_META[contentType] || { label: contentType, chipBg: T.pressLight, chipFg: T.ink };
  return (
    <Box
      component="span"
      sx={{
        display: "inline-flex", alignItems: "center", gap: "6px", fontSize: 11, fontWeight: 700,
        letterSpacing: ".02em", px: "9px", py: "4px", borderRadius: "999px", whiteSpace: "nowrap",
        flexShrink: 0, bgcolor: meta.chipBg, color: meta.chipFg,
      }}
    >
      {meta.label}
    </Box>
  );
}

function sessionLabel(session) {
  const date = session.sessionDate ? ` — ${session.sessionDate}` : "";
  return `${session.topic || `Session ${session.id}`}${date}`;
}

/** Dialog for creating or renaming a learning path. */
function PathDialog({ open, initial, onClose, onSave }) {
  const [name, setName] = React.useState(initial?.name || "");
  const [description, setDescription] = React.useState(initial?.description || "");

  React.useEffect(() => {
    if (open) {
      setName(initial?.name || "");
      setDescription(initial?.description || "");
    }
  }, [open, initial]);

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle sx={{ fontWeight: 700, fontFamily: T.display }}>
        {initial ? "Rename learning path" : "New learning path"}
      </DialogTitle>
      <DialogContent sx={{ display: "flex", flexDirection: "column", gap: 2, pt: 1 }}>
        <TextField label="Path name" value={name} onChange={(e) => setName(e.target.value)} autoFocus fullWidth />
        <TextField
          label="Description (optional)"
          value={description}
          onChange={(e) => setDescription(e.target.value)}
          fullWidth
          multiline
          minRows={2}
        />
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} sx={{ textTransform: "none" }}>Cancel</Button>
        <Button
          variant="contained"
          disabled={!name.trim()}
          onClick={() => onSave({ name: name.trim(), description: description.trim() || null })}
          sx={{ bgcolor: T.primary, textTransform: "none", fontWeight: 700, borderRadius: "8px" }}
        >
          Save
        </Button>
      </DialogActions>
    </Dialog>
  );
}

export default function VirtualLearningTab({ canEdit, sessions = [] }) {
  const { id: courseId } = useParams();
  const [allPaths, setAllPaths] = React.useState([]);
  const [scope, setScope] = React.useState(GLOBAL_SCOPE);
  const [selectedPathId, setSelectedPathId] = React.useState(null);
  const [plan, setPlan] = React.useState(null);
  const [items, setItems] = React.useState([]);
  const [available, setAvailable] = React.useState([]);
  const [pathsLoading, setPathsLoading] = React.useState(true);
  const [planLoading, setPlanLoading] = React.useState(false);
  const [error, setError] = React.useState(null);
  const [draggedId, setDraggedId] = React.useState(null);
  const [draggedAvail, setDraggedAvail] = React.useState(null);
  const [dropTargetId, setDropTargetId] = React.useState(null);
  const [listDragOver, setListDragOver] = React.useState(false);
  const [pathDialogOpen, setPathDialogOpen] = React.useState(false);
  const [pathDialogInitial, setPathDialogInitial] = React.useState(null);
  const [pathMenuAnchor, setPathMenuAnchor] = React.useState(null);
  const [deleteConfirmPath, setDeleteConfirmPath] = React.useState(null);

  const scopeSessionId = scope === GLOBAL_SCOPE ? null : scope;
  const pathsInScope = React.useMemo(
    () => allPaths.filter((p) => (p.sessionId ?? null) === scopeSessionId),
    [allPaths, scopeSessionId],
  );

  const loadPaths = React.useCallback(async () => {
    const list = await fetchLearningPaths(courseId);
    setAllPaths(list);
    return list;
  }, [courseId]);

  const loadPlanData = React.useCallback(
    (planId) => {
      if (!planId) {
        setPlan(null);
        setItems([]);
        setAvailable([]);
        return;
      }
      setPlanLoading(true);
      Promise.all([fetchLearningPlan(courseId, planId), fetchAvailablePlanContent(courseId, planId)])
        .then(([planData, availableData]) => {
          setPlan(planData);
          setItems(Array.isArray(planData?.items) ? planData.items : []);
          setAvailable([
            ...(availableData?.documents || []).map((a) => ({ ...a, contentType: "DOCUMENT" })),
            ...(availableData?.tests || []).map((a) => ({ ...a, contentType: "TEST" })),
            ...(availableData?.skillSheets || []).map((a) => ({ ...a, contentType: "SKILL_SHEET" })),
          ]);
          setError(null);
        })
        .catch(() => setError("Failed to load the learning path."))
        .finally(() => setPlanLoading(false));
    },
    [courseId],
  );

  React.useEffect(() => {
    let cancelled = false;
    setPathsLoading(true);
    loadPaths()
      .catch(() => {
        if (!cancelled) setError("Failed to load learning paths.");
      })
      .finally(() => {
        if (!cancelled) setPathsLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [loadPaths]);

  React.useEffect(() => {
    if (pathsInScope.length === 0) {
      setSelectedPathId(null);
      return;
    }
    setSelectedPathId((prev) => (pathsInScope.some((p) => p.id === prev) ? prev : pathsInScope[0].id));
  }, [scope, allPaths, pathsInScope]);

  React.useEffect(() => {
    loadPlanData(selectedPathId);
  }, [selectedPathId, loadPlanData]);

  const openCreate = () => {
    setPathDialogInitial(null);
    setPathDialogOpen(true);
  };

  const handleCreatePath = async ({ name, description }) => {
    try {
      const created = await createLearningPath(courseId, { name, description, sessionId: scopeSessionId });
      setPathDialogOpen(false);
      await loadPaths();
      setSelectedPathId(created.id);
    } catch {
      setError("Failed to create learning path.");
    }
  };

  const handleRenamePath = async ({ name, description }) => {
    try {
      await renameLearningPath(courseId, selectedPathId, { name, description });
      setPathDialogOpen(false);
      await loadPaths();
    } catch {
      setError("Failed to rename learning path.");
    }
  };

  const handleDeletePath = async () => {
    const pathId = deleteConfirmPath;
    setDeleteConfirmPath(null);
    try {
      await deleteLearningPath(courseId, pathId);
      await loadPaths();
    } catch {
      setError("Failed to delete learning path.");
    }
  };

  const persistOrder = async (newItems) => {
    const prev = items;
    setItems(newItems.map((item, i) => ({ ...item, displayOrder: i })));
    try {
      const updated = await reorderLearningPlanItems(courseId, selectedPathId, newItems.map((i) => i.id));
      if (Array.isArray(updated)) setItems(updated);
    } catch {
      setItems(prev);
    }
  };

  const moveItem = (index, delta) => {
    const target = index + delta;
    if (target < 0 || target >= items.length) return;
    const next = items.slice();
    [next[index], next[target]] = [next[target], next[index]];
    persistOrder(next);
  };

  const insertBefore = (dragged, beforeId) => {
    const current = items.filter((i) => i.id !== dragged.id);
    const insertAt = beforeId == null ? current.length : current.findIndex((i) => i.id === beforeId);
    const next = [
      ...current.slice(0, insertAt === -1 ? current.length : insertAt),
      dragged,
      ...current.slice(insertAt === -1 ? current.length : insertAt),
    ];
    persistOrder(next);
  };

  const handleAdd = async (avail, beforeId = null) => {
    try {
      const created = await addLearningPlanItem(courseId, selectedPathId, {
        contentType: avail.contentType,
        contentRefId: avail.id,
      });
      setAvailable((prev) => prev.filter((a) => !(a.contentType === avail.contentType && a.id === avail.id)));
      setItems((prev) => [...prev, created]);
      if (beforeId != null) insertBefore(created, beforeId);
    } catch {
      setError("Failed to add item to the path.");
    }
  };

  const handleListDrop = (beforeId = null) => {
    if (draggedAvail) {
      const avail = draggedAvail;
      setDraggedAvail(null);
      handleAdd(avail, beforeId);
      return;
    }
    if (draggedId != null) {
      const item = items.find((i) => i.id === draggedId);
      setDraggedId(null);
      if (!item) return;
      insertBefore(item, beforeId);
    }
  };

  const handleRemove = async (item) => {
    const prev = items;
    setItems((list) => list.filter((i) => i.id !== item.id));
    try {
      await removeLearningPlanItem(courseId, selectedPathId, item.id);
      setAvailable((list) => [...list, { id: item.contentRefId, contentType: item.contentType, title: item.title }]);
    } catch {
      setItems(prev);
    }
  };

  const handleGatingToggle = async (item, checked) => {
    setItems((list) => list.map((i) => (i.id === item.id ? { ...i, requiresPreviousComplete: checked } : i)));
    try {
      await setItemGating(courseId, selectedPathId, item.id, checked);
    } catch {
      setItems((list) => list.map((i) => (i.id === item.id ? { ...i, requiresPreviousComplete: !checked } : i)));
    }
  };

  const handleDualShowToggle = async (item, checked) => {
    setItems((list) => list.map((i) => (i.id === item.id ? { ...i, dualShow: checked } : i)));
    try {
      await setItemDualShow(courseId, selectedPathId, item.id, checked);
    } catch {
      setItems((list) => list.map((i) => (i.id === item.id ? { ...i, dualShow: !checked } : i)));
    }
  };

  const isInstructorPaced = plan?.pacingMode === "INSTRUCTOR_PACED";

  const handleTogglePacing = async (checked) => {
    const next = checked ? "INSTRUCTOR_PACED" : "SELF_PACED";
    const prevMode = plan?.pacingMode;
    setPlan((p) => ({ ...p, pacingMode: next }));
    try {
      await setPlanPacing(courseId, selectedPathId, next);
      loadPlanData(selectedPathId);
    } catch {
      setPlan((p) => ({ ...p, pacingMode: prevMode }));
      setError("Failed to change pacing mode.");
    }
  };

  const handleUnlockNext = async () => {
    try {
      await unlockNextPlanItem(courseId, selectedPathId);
      loadPlanData(selectedPathId);
    } catch {
      setError("Failed to unlock the next item.");
    }
  };

  if (pathsLoading) {
    return (
      <Box sx={{ display: "flex", justifyContent: "center", py: 6 }}>
        <CircularProgress size={28} />
      </Box>
    );
  }

  const sorted = items.slice().sort((a, b) => (a.displayOrder ?? 0) - (b.displayOrder ?? 0));
  const isDragActive = Boolean(draggedAvail || draggedId != null);
  const unlockedThrough = plan?.unlockedThroughOrder ?? 0;
  const unlockDisabled = unlockedThrough >= items.length;

  return (
    <Box>
      {error && (
        <Alert severity="error" onClose={() => setError(null)} sx={{ mb: 2 }}>
          {error}
        </Alert>
      )}

      {/* Section scope */}
      <Box sx={{ mb: "28px" }}>
        <Typography
          component="label"
          sx={{ fontSize: "12.5px", fontWeight: 600, color: T.ink65, mb: "8px", display: "block" }}
        >
          Section
        </Typography>
        <Select
          size="small"
          value={scope}
          onChange={(e) => setScope(e.target.value)}
          sx={{
            minWidth: 260, height: 40, borderRadius: "6px", bgcolor: "#fff",
            fontSize: 14, fontWeight: 500, color: T.ink,
            "& .MuiOutlinedInput-notchedOutline": { borderColor: T.line },
            "&:hover .MuiOutlinedInput-notchedOutline": { borderColor: T.lineCool },
            "&.Mui-focused .MuiOutlinedInput-notchedOutline": { borderColor: T.violetLink, borderWidth: "1px" },
          }}
        >
          <MenuItem value={GLOBAL_SCOPE}>Global</MenuItem>
          {sessions.map((s) => (
            <MenuItem key={s.id} value={s.id}>{sessionLabel(s)}</MenuItem>
          ))}
        </Select>
      </Box>

      {/* Empty section */}
      {pathsInScope.length === 0 ? (
        <Box
          sx={{
            border: `1px dashed ${T.line}`, borderRadius: "12px", p: "56px 24px", textAlign: "center",
            bgcolor: T.panelBg,
          }}
        >
          <Box
            sx={{
              width: 52, height: 52, borderRadius: "10px", bgcolor: "#fff", border: `1px solid ${T.line}`,
              display: "grid", placeItems: "center", mx: "auto", mb: "16px", color: T.ink55,
            }}
          >
            <IconRoute />
          </Box>
          <Typography sx={{ fontSize: 17, fontWeight: 600, color: T.ink, fontFamily: T.display, mb: "6px" }}>
            No learning paths yet
          </Typography>
          <Typography sx={{ fontSize: "13.5px", color: T.ink55, maxWidth: 340, mx: "auto", mb: "20px" }}>
            This section has no learning paths. Create one to bundle documents, tests, and skill sheets into a
            step-by-step track for students.
          </Typography>
          {canEdit && (
            <Button
              onClick={openCreate}
              sx={{
                height: 38, px: "16px", borderRadius: "8px", bgcolor: T.primary, color: "#fff",
                fontSize: 13, fontWeight: 700, letterSpacing: ".2px", textTransform: "uppercase",
                lineHeight: 1.14, whiteSpace: "nowrap", flexShrink: 0,
                display: "inline-flex", alignItems: "center", gap: "8px",
                "&:hover": { bgcolor: "#0e0a19" },
              }}
            >
              <IconPlus />
              Create first path
            </Button>
          )}
        </Box>
      ) : (
        <>
          {/* Path bar */}
          <Box
            sx={{
              display: "flex", alignItems: "center", justifyContent: "space-between", gap: "16px",
              rowGap: "10px", borderBottom: `1px solid ${T.line}`, mb: "24px", flexWrap: "wrap",
            }}
          >
            <Box sx={{ display: "flex", alignItems: "center", flexWrap: "wrap", flex: "1 1 auto", minWidth: 0 }}>
              {pathsInScope.map((p) => {
                const active = p.id === selectedPathId;
                return (
                  <Box
                    key={p.id}
                    onClick={() => setSelectedPathId(p.id)}
                    sx={{
                      position: "relative", height: 46, px: "4px", mr: "22px", mb: "-1px",
                      display: "flex", alignItems: "center", gap: "8px", whiteSpace: "nowrap",
                      fontSize: 15, fontWeight: active ? 600 : 500,
                      color: active ? T.primary : T.ink, opacity: active ? 1 : 0.55,
                      borderBottom: `2px solid ${active ? T.primary : "transparent"}`,
                      cursor: active ? "default" : "pointer",
                      transition: "color .12s, opacity .12s, border-color .12s",
                      "&:hover": { opacity: active ? 1 : 0.8 },
                    }}
                  >
                    {p.name}
                    <Box
                      component="span"
                      sx={{
                        fontSize: 11, fontWeight: 600, px: "7px", py: "1px", borderRadius: "999px",
                        bgcolor: active ? T.primaryTint : T.pressLight,
                        color: active ? T.primary : T.ink, opacity: active ? 1 : 0.7,
                      }}
                    >
                      {p.itemCount}
                    </Box>
                  </Box>
                );
              })}
            </Box>

            <Box
              sx={{
                display: "flex", alignItems: "center", gap: "10px", pb: "10px",
                flexWrap: "wrap", flexShrink: 0, justifyContent: "flex-end", ml: "auto",
              }}
            >
              {canEdit && selectedPathId != null && (
                <Box
                  sx={{ display: "flex", alignItems: "center", gap: "9px", fontSize: 13, fontWeight: 500, color: T.ink, whiteSpace: "nowrap" }}
                  title="When on, students unlock items one at a time as the instructor releases them"
                >
                  <Toggle checked={isInstructorPaced} onChange={handleTogglePacing} />
                  Instructor-paced
                </Box>
              )}

              {canEdit && isInstructorPaced && (
                <Button
                  onClick={handleUnlockNext}
                  disabled={unlockDisabled}
                  sx={{
                    height: 34, px: "13px", borderRadius: "8px", border: `1px solid ${T.line}`,
                    bgcolor: "#fff", color: T.ink, fontSize: "11.5px", fontWeight: 500, textTransform: "none",
                    lineHeight: 1.14, whiteSpace: "nowrap", flexShrink: 0,
                    display: "inline-flex", alignItems: "center", gap: "8px",
                    "&:hover": { bgcolor: T.pressLight },
                    "&.Mui-disabled": { opacity: 0.4 },
                  }}
                >
                  <IconLock />
                  Unlock next item ({Math.min(unlockedThrough, items.length)}/{items.length} unlocked)
                </Button>
              )}

              {canEdit && selectedPathId != null && (
                <>
                  <IconButton
                    onClick={(e) => setPathMenuAnchor(e.currentTarget)}
                    aria-label="Path options"
                    sx={{
                      width: 34, height: 34, borderRadius: "8px", border: `1px solid ${T.line}`,
                      bgcolor: "#fff", color: T.ink, opacity: 0.7,
                      "&:hover": { bgcolor: T.pressLight, opacity: 1 },
                    }}
                  >
                    <IconMore />
                  </IconButton>
                  <Menu
                    anchorEl={pathMenuAnchor}
                    open={Boolean(pathMenuAnchor)}
                    onClose={() => setPathMenuAnchor(null)}
                    anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
                    transformOrigin={{ vertical: "top", horizontal: "right" }}
                    slotProps={{ paper: { sx: { borderRadius: "10px", boxShadow: T.shadowMd, minWidth: 160, p: "6px" } } }}
                  >
                    <MenuItem
                      onClick={() => {
                        const current = pathsInScope.find((p) => p.id === selectedPathId);
                        setPathDialogInitial(current || null);
                        setPathDialogOpen(true);
                        setPathMenuAnchor(null);
                      }}
                      sx={{ gap: "9px", borderRadius: "6px", fontSize: "13.5px", fontWeight: 500 }}
                    >
                      <IconPencil />
                      Rename
                    </MenuItem>
                    <MenuItem
                      onClick={() => {
                        setDeleteConfirmPath(selectedPathId);
                        setPathMenuAnchor(null);
                      }}
                      sx={{ gap: "9px", borderRadius: "6px", fontSize: "13.5px", fontWeight: 500, color: T.danger, "&:hover": { bgcolor: "rgba(250,127,170,.12)" } }}
                    >
                      <IconTrash />
                      Delete
                    </MenuItem>
                  </Menu>
                </>
              )}

              {canEdit && (
                <Button
                  onClick={openCreate}
                  sx={{
                    height: 34, px: "13px", borderRadius: "8px", bgcolor: T.primary, color: "#fff",
                    fontSize: "11.5px", fontWeight: 700, letterSpacing: ".2px", textTransform: "uppercase",
                    lineHeight: 1.14, whiteSpace: "nowrap", flexShrink: 0, minWidth: "auto",
                    display: "inline-flex", alignItems: "center", gap: "8px",
                    "&:hover": { bgcolor: "#0e0a19" },
                  }}
                >
                  <IconPlus />
                  New path
                </Button>
              )}
            </Box>
          </Box>

          {selectedPathId != null && planLoading && (
            <Box sx={{ display: "flex", justifyContent: "center", py: 6 }}>
              <CircularProgress size={28} />
            </Box>
          )}

          {selectedPathId != null && !planLoading && (
            <Box
              sx={{
                display: "grid",
                gridTemplateColumns: { xs: "1fr", md: "300px 1fr" },
                gap: "22px",
                alignItems: "start",
              }}
            >
              {/* Content bank */}
              <Box sx={{ bgcolor: T.panelBg, border: `1px solid ${T.line}`, borderRadius: "12px", p: "18px" }}>
                <Typography sx={{ fontSize: "14.5px", fontWeight: 600, color: T.ink, fontFamily: T.display }}>
                  Available content
                </Typography>
                <Typography sx={{ fontSize: 12, color: T.ink55, m: "4px 0 16px" }}>
                  Drag an item into the order on the right, or click Add.
                </Typography>

                {available.length === 0 ? (
                  <Typography sx={{ fontSize: "12.5px", color: T.ink55, textAlign: "center", p: "24px 8px" }}>
                    All course content has been placed into a path.
                  </Typography>
                ) : (
                  TYPE_ORDER.map((ct) => {
                    const group = available.filter((a) => a.contentType === ct);
                    const meta = TYPE_META[ct];
                    return (
                      <Box key={ct} sx={{ mb: "18px", "&:last-of-type": { mb: 0 } }}>
                        <Box
                          sx={{
                            fontSize: 11, fontWeight: 700, letterSpacing: ".08em", textTransform: "uppercase",
                            color: T.ink45, mb: "8px", display: "flex", alignItems: "center", gap: "7px",
                          }}
                        >
                          <Box sx={{ width: 7, height: 7, borderRadius: "50%", bgcolor: meta.dot }} />
                          {meta.bankLabel}
                        </Box>
                        {group.length === 0 ? (
                          <Typography sx={{ fontSize: 12, color: T.ink40, fontStyle: "italic", p: "4px 0 10px" }}>
                            None left unused
                          </Typography>
                        ) : (
                          group.map((a) => (
                            <Box
                              key={`${a.contentType}-${a.id}`}
                              draggable={canEdit}
                              onDragStart={() => setDraggedAvail(a)}
                              onDragEnd={() => setDraggedAvail(null)}
                              sx={{
                                display: "flex", alignItems: "center", gap: "10px", py: "9px",
                                borderBottom: `1px solid ${T.line}`, "&:last-of-type": { borderBottom: 0 },
                                cursor: canEdit ? "grab" : "default",
                                opacity: draggedAvail === a ? 0.4 : 1,
                                "&:active": { cursor: canEdit ? "grabbing" : "default" },
                              }}
                            >
                              <Typography
                                sx={{
                                  flex: 1, minWidth: 0, fontSize: 13, fontWeight: 500, color: T.ink,
                                  overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap",
                                }}
                              >
                                {a.title || "Untitled"}
                              </Typography>
                              {canEdit && (
                                <Box
                                  component="button"
                                  onClick={() => handleAdd(a)}
                                  sx={{
                                    flexShrink: 0, border: 0, background: "none", cursor: "pointer",
                                    fontSize: "12.5px", fontWeight: 600, color: T.violetLink, p: "2px 4px",
                                    fontFamily: "inherit",
                                    "&:hover": { color: T.primary, textDecoration: "underline" },
                                  }}
                                >
                                  Add
                                </Box>
                              )}
                            </Box>
                          ))
                        )}
                      </Box>
                    );
                  })
                )}
              </Box>

              {/* Order panel */}
              <Box
                onDragOver={(e) => {
                  if (canEdit && isDragActive) {
                    e.preventDefault();
                    setListDragOver(true);
                  }
                }}
                onDragLeave={() => setListDragOver(false)}
                onDrop={(e) => {
                  e.preventDefault();
                  setListDragOver(false);
                  if (canEdit && !e.target.closest?.("[data-order-row]")) handleListDrop(null);
                }}
                sx={{
                  bgcolor: "#fff", border: `1px solid ${listDragOver && canEdit ? T.violetLink : T.line}`,
                  borderRadius: "12px", minHeight: 420, overflow: "hidden",
                  boxShadow: listDragOver && canEdit ? "0 0 0 3px rgba(106,95,193,.12)" : "none",
                  transition: "border-color .12s, box-shadow .12s",
                }}
              >
                {sorted.length === 0 ? (
                  <Box
                    sx={{
                      display: "flex", alignItems: "center", justifyContent: "center", height: 420,
                      textAlign: "center", px: "30px", fontSize: "13.5px", color: T.ink40,
                    }}
                  >
                    Drag content here, or click Add on the left, to build this path&apos;s order.
                  </Box>
                ) : (
                  sorted.map((item, idx) => {
                    const locked = isInstructorPaced && idx > unlockedThrough;
                    return (
                      <Box
                        key={item.id}
                        data-order-row
                        draggable={canEdit}
                        onDragStart={() => setDraggedId(item.id)}
                        onDragEnd={() => {
                          setDraggedId(null);
                          setDropTargetId(null);
                        }}
                        onDragOver={(e) => {
                          if (canEdit && isDragActive) {
                            e.preventDefault();
                            setDropTargetId(item.id);
                          }
                        }}
                        onDrop={(e) => {
                          e.preventDefault();
                          e.stopPropagation();
                          setDropTargetId(null);
                          if (canEdit) handleListDrop(item.id);
                        }}
                        sx={{
                          display: "flex", flexDirection: "column", gap: "10px", p: "14px 18px",
                          borderBottom: `1px solid ${T.line}`, "&:last-of-type": { borderBottom: 0 },
                          bgcolor: "#fff",
                          opacity: draggedId === item.id ? 0.35 : 1,
                          boxShadow: dropTargetId === item.id && draggedId !== item.id ? `inset 0 2px 0 ${T.violetLink}` : "none",
                        }}
                      >
                        <Box sx={{ display: "flex", alignItems: "center", gap: "12px", minWidth: 0 }}>
                          <Box
                            sx={{
                              color: T.ink35, display: "flex", flexShrink: 0,
                              cursor: canEdit ? "grab" : "default", "&:active": { cursor: canEdit ? "grabbing" : "default" },
                            }}
                          >
                            <IconDrag />
                          </Box>
                          <Box sx={{ display: "flex", flexDirection: "column", gap: "1px", flexShrink: 0 }}>
                            <Box
                              component="button"
                              disabled={!canEdit || idx === 0}
                              onClick={() => moveItem(idx, -1)}
                              sx={{
                                width: 20, height: 12, border: 0, background: "transparent", color: T.ink, opacity: 0.55,
                                cursor: "pointer", display: "grid", placeItems: "center", borderRadius: "3px",
                                "&:hover:not(:disabled)": { bgcolor: T.pressLight, opacity: 1 },
                                "&:disabled": { opacity: 0.25, cursor: "default" },
                              }}
                            >
                              <IconUp />
                            </Box>
                            <Box
                              component="button"
                              disabled={!canEdit || idx === sorted.length - 1}
                              onClick={() => moveItem(idx, 1)}
                              sx={{
                                width: 20, height: 12, border: 0, background: "transparent", color: T.ink, opacity: 0.55,
                                cursor: "pointer", display: "grid", placeItems: "center", borderRadius: "3px",
                                "&:hover:not(:disabled)": { bgcolor: T.pressLight, opacity: 1 },
                                "&:disabled": { opacity: 0.25, cursor: "default" },
                              }}
                            >
                              <IconDown />
                            </Box>
                          </Box>
                          <Box
                            component="span"
                            sx={{ fontFamily: T.display, fontSize: 13, fontWeight: 600, color: T.ink40, flexShrink: 0, width: 16 }}
                          >
                            {idx + 1}
                          </Box>
                          <TypeChip contentType={item.contentType} />
                          <Typography
                            sx={{
                              fontSize: 14, fontWeight: 600, color: T.ink, flex: 1, minWidth: 0,
                              overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap",
                            }}
                            title={item.title || "Untitled"}
                          >
                            {item.title || "Untitled"}
                            {locked && (
                              <Box component="span" sx={{ opacity: 0.4, fontWeight: 600 }}> — locked</Box>
                            )}
                          </Typography>
                          {canEdit && (
                            <IconButton
                              onClick={() => handleRemove(item)}
                              aria-label="Remove from path"
                              sx={{
                                width: 34, height: 34, borderRadius: "8px", border: `1px solid ${T.line}`, bgcolor: "#fff",
                                color: T.ink, opacity: 0.7, flexShrink: 0,
                                "&:hover": { bgcolor: T.dangerBg, color: T.danger, opacity: 1 },
                              }}
                            >
                              <IconTrash />
                            </IconButton>
                          )}
                        </Box>

                        <Box
                          sx={{
                            display: "flex", alignItems: "center", gap: "18px", flexWrap: "wrap",
                            pl: { xs: 0, md: "64px" },
                          }}
                        >
                          <Box sx={{ display: "flex", alignItems: "center", gap: "8px", fontSize: 12, fontWeight: 500, color: T.ink65, whiteSpace: "nowrap" }}>
                            <Toggle
                              checked={Boolean(item.requiresPreviousComplete)}
                              disabled={!canEdit || idx === 0}
                              onChange={(checked) => handleGatingToggle(item, checked)}
                            />
                            Require previous complete
                          </Box>
                          <Box sx={{ display: "flex", alignItems: "center", gap: "8px", fontSize: 12, fontWeight: 500, color: T.ink65, whiteSpace: "nowrap" }}>
                            <Toggle
                              checked={Boolean(item.dualShow)}
                              disabled={!canEdit}
                              onChange={(checked) => handleDualShowToggle(item, checked)}
                            />
                            Also show standalone
                          </Box>
                        </Box>
                      </Box>
                    );
                  })
                )}
              </Box>
            </Box>
          )}
        </>
      )}

      <PathDialog
        open={pathDialogOpen}
        initial={pathDialogInitial}
        onClose={() => setPathDialogOpen(false)}
        onSave={pathDialogInitial ? handleRenamePath : handleCreatePath}
      />

      <Dialog open={Boolean(deleteConfirmPath)} onClose={() => setDeleteConfirmPath(null)} maxWidth="xs" fullWidth>
        <DialogTitle sx={{ fontWeight: 700, fontFamily: T.display }}>Delete this learning path?</DialogTitle>
        <DialogContent>
          <Typography variant="body2">
            This will permanently remove the path and all of its items. This cannot be undone.
          </Typography>
        </DialogContent>
        <DialogActions sx={{ px: 2, pb: 2 }}>
          <Button onClick={() => setDeleteConfirmPath(null)} sx={{ textTransform: "none" }}>Cancel</Button>
          <Button
            color="error"
            variant="contained"
            onClick={handleDeletePath}
            sx={{ textTransform: "none", fontWeight: 700, borderRadius: "8px" }}
          >
            Delete
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}
